import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PlatformSendingStatus } from '@dashboard/generated/wp/authentication';
import { EmailMessagesFilterInput, EmailMessageTimePeriodFilter } from '@dashboard/generated/wp/platform';
import {
  castEmailMessageEnvironmentFilter,
  castEmailMessageEventTypeFilter,
  castEmailMessageTimePeriodFilter,
  DEFAULT_EMAIL_MESSAGE_ENVIRONMENT_FILTER,
  DEFAULT_EMAIL_MESSAGE_EVENT_TYPE_FILTER,
} from '@dashboard/graphql/platform/cast-value';
import useCurrentWpPlatform from '@dashboard/hooks/useCurrentWpPlatform';

type TStatus = readonly [EmailMessagesFilterInput, (val: EmailMessagesFilterInput) => void];

export function useEmailMessagesFilters(): TStatus {
  const { data } = useCurrentWpPlatform();
  const isLive = data?.Platform.sendingStatus === PlatformSendingStatus.LiveMode;

  const [searchParams, setSearchParams] = useSearchParams();

  let defaultTimePeriod = EmailMessageTimePeriodFilter.All;
  if (isLive) {
    defaultTimePeriod = EmailMessageTimePeriodFilter.Week;
  }

  const filter = useMemo<EmailMessagesFilterInput>(
    () => ({
      environment: castEmailMessageEnvironmentFilter(searchParams.get('environment')),
      timePeriod: castEmailMessageTimePeriodFilter(searchParams.get('timePeriod'), defaultTimePeriod),
      eventType: castEmailMessageEventTypeFilter(searchParams.get('eventType')),
      sourceEmailMessagesBatchId: searchParams.get('batchId') ?? null,
      query: searchParams.get('query') ?? null,
    }),
    [searchParams, defaultTimePeriod]
  );

  const setFilter = useCallback(
    (filter: EmailMessagesFilterInput) => {
      searchParams.delete('page');

      if (filter.environment === DEFAULT_EMAIL_MESSAGE_ENVIRONMENT_FILTER) {
        searchParams.delete('environment');
      } else if (filter.environment) {
        searchParams.set('environment', filter.environment);
      }
      if (filter.timePeriod === defaultTimePeriod) {
        searchParams.delete('timePeriod');
      } else if (filter.timePeriod) {
        searchParams.set('timePeriod', filter.timePeriod);
      }

      if (filter.eventType === DEFAULT_EMAIL_MESSAGE_EVENT_TYPE_FILTER) {
        searchParams.delete('eventType');
      } else if (filter.eventType) {
        searchParams.set('eventType', filter.eventType);
      }

      if (filter.query === null) {
        searchParams.delete('query');
      } else if (typeof filter.query === 'string') {
        if (filter.query.trim().length === 0) {
          searchParams.delete('query');
        } else {
          searchParams.set('query', filter.query);
        }
      }

      if (!filter.sourceEmailMessagesBatchId) {
        searchParams.delete('batchId');
      } else {
        searchParams.set('batchId', filter.sourceEmailMessagesBatchId);
      }

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams, defaultTimePeriod]
  );

  return useMemo(() => [filter, setFilter], [filter, setFilter]);
}
